(function() {
  'use strict';

  let technicalSheetModal = null;
  let technicalSheetModalCloseHandler = null;

  function getCloseAriaLabel() {
    const lang = (document.documentElement.lang || '').toLowerCase();
    return lang.startsWith('fr') ? 'Fermer' : 'Close';
  }

  function createTechnicalSheetModal() {
    if (technicalSheetModal) return technicalSheetModal;
    const modal = document.createElement('div');
    modal.className = 'technical-sheet-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', getCloseAriaLabel());
    modal.innerHTML = [
      '<div class="technical-sheet-modal__overlay" data-close></div>',
      '<div class="technical-sheet-modal__content">',
        '<button type="button" class="technical-sheet-modal__close focus-inset" data-close aria-label="' + getCloseAriaLabel() + '">',
          '<span aria-hidden="true">&times;</span>',
        '</button>',
        '<img class="technical-sheet-modal__image" src="" alt="" loading="lazy">',
      '</div>'
    ].join('');
    document.body.appendChild(modal);
    technicalSheetModal = modal;

    const closeModal = () => {
      modal.classList.remove('technical-sheet-modal--open');
      document.body.classList.remove('technical-sheet-modal-open');
      document.removeEventListener('keydown', technicalSheetModalCloseHandler);
      technicalSheetModalCloseHandler = null;
    };

    modal.querySelectorAll('[data-close]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        closeModal();
      });
    });
    modal.querySelector('.technical-sheet-modal__content').addEventListener('click', (e) => {
      e.stopPropagation();
    });

    technicalSheetModalCloseHandler = (e) => {
      if (e.key === 'Escape') closeModal();
    };

    return modal;
  }

  function openTechnicalSheetModal(src, alt) {
    const modal = createTechnicalSheetModal();
    const img = modal.querySelector('.technical-sheet-modal__image');
    img.src = src || '';
    img.alt = alt || 'Technical sheet';
    modal.classList.add('technical-sheet-modal--open');
    document.body.classList.add('technical-sheet-modal-open');
    document.addEventListener('keydown', technicalSheetModalCloseHandler);
  }

  function bindTechnicalSheetClicks() {
    document.querySelectorAll('.technical-sheet__image-wrapper--clickable').forEach((wrapper) => {
      if (wrapper.dataset.technicalSheetBound) return;
      wrapper.dataset.technicalSheetBound = 'true';
      const img = wrapper.querySelector('.technical-sheet__image');
      if (!img) return;
      wrapper.addEventListener('click', (e) => {
        e.preventDefault();
        const src = img.currentSrc || img.src;
        if (!src) return;
        openTechnicalSheetModal(src, img.alt || 'Technical sheet');
      });
      wrapper.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          wrapper.click();
        }
      });
    });
  }

  /**
   * Updates the technical sheet image based on the selected variant
   * @param {Object} event - The variant:change event
   */
  const updateTechnicalSheet = (event) => {
    const sectionId = event.detail.sectionId;
    const variant = event.detail.variant;
    
    if (!variant) return;

    const technicalSheetImg = document.getElementById(`technical-sheet-${sectionId}`);
    if (!technicalSheetImg) return;

    // Get product technical sheet from the script tag
    const productSheetScript = document.getElementById(`product-technical-sheet-${sectionId}`);
    const productTechnicalSheet = productSheetScript ? JSON.parse(productSheetScript.textContent) : null;

    // Priority: variant metafield > product metafield
    // Handle both string and object formats for metafields
    let variantSheet = variant.metafields?.custom?.technical_sheet;
    if (variantSheet && typeof variantSheet === 'object') {
      variantSheet = variantSheet.value || variantSheet.url || null;
    }
    
    let productSheet = productTechnicalSheet;
    if (productSheet && typeof productSheet === 'object') {
      productSheet = productSheet.value || productSheet.url || null;
    }
    
    const technicalSheetUrl = variantSheet || productSheet || '';

    if (technicalSheetUrl && typeof technicalSheetUrl === 'string') {
      technicalSheetImg.src = technicalSheetUrl;
      technicalSheetImg.style.display = '';
      // Show the wrapper if it was hidden
      const wrapper = technicalSheetImg.closest('.technical-sheet__image-wrapper');
      if (wrapper) {
        wrapper.style.display = '';
      }
    } else {
      // Only hide if there's no URL, but keep the container visible
      technicalSheetImg.style.display = 'none';
    }
  };

  /**
   * Initialize technical sheet on page load
   */
  const initTechnicalSheet = () => {
    // Find all technical sheet images on the page
    const technicalSheetImages = document.querySelectorAll('[id^="technical-sheet-"]');
    
    technicalSheetImages.forEach((img) => {
      const sectionId = img.id.replace('technical-sheet-', '');
      
      // Check if image already has a valid src from Liquid template
      const currentSrc = img.src || img.getAttribute('src') || '';
      const hasValidSrc = currentSrc.trim() !== '' && !currentSrc.includes('data:image') && currentSrc !== window.location.href;
      
      // Get the variant selects element to find current variant
      const variantSelects = document.querySelector(`variant-selects[data-section="${sectionId}"], variant-radios[data-section="${sectionId}"]`);
      
      let variant = null;
      
      // Try to get current variant from variant selects
      if (variantSelects) {
        // Wait a bit for variant data to be loaded
        if (variantSelects.currentVariant) {
          variant = variantSelects.currentVariant;
        } else if (variantSelects.getVariantData && variantSelects.getVariantData().length > 0) {
          // Try to get the first available variant or selected variant
          const variantData = variantSelects.getVariantData();
          const selectedVariantId = document.querySelector(`#product-form-${sectionId} input[name="id"]`)?.value;
          if (selectedVariantId) {
            variant = variantData.find(v => v.id === parseInt(selectedVariantId));
          }
          if (!variant && variantData.length > 0) {
            variant = variantData.find(v => v.available) || variantData[0];
          }
        }
      }
      
      if (variant) {
        // Create a synthetic event for initial load
        const event = {
          detail: {
            variant: variant,
            sectionId: sectionId
          }
        };
        updateTechnicalSheet(event);
      } else {
        // Fallback: use product metafield if no variant is selected
        const productSheetScript = document.getElementById(`product-technical-sheet-${sectionId}`);
        const productTechnicalSheet = productSheetScript ? JSON.parse(productSheetScript.textContent) : null;
        
        // Handle object format for product metafield
        let productSheet = productTechnicalSheet;
        if (productSheet && typeof productSheet === 'object') {
          productSheet = productSheet.value || productSheet.url || null;
        }
        
        if (productSheet && typeof productSheet === 'string') {
          img.src = productSheet;
          img.style.display = '';
        } else if (hasValidSrc) {
          // Keep the image visible if it already has a valid src from Liquid
          img.style.display = '';
          // Ensure wrapper is visible
          const wrapper = img.closest('.technical-sheet__image-wrapper');
          if (wrapper) {
            wrapper.style.display = '';
          }
        } else {
          // Only hide if there's truly no image source
          img.style.display = 'none';
        }
      }
    });
    bindTechnicalSheetClicks();
  };

  // Listen for variant changes
  document.addEventListener('variant:change', updateTechnicalSheet);

  // Initialize on page load (with a small delay to ensure variants are loaded)
  const initialize = () => {
    // Small delay to ensure variant data is loaded
    setTimeout(initTechnicalSheet, 100);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Re-initialize when section is loaded in theme editor
  document.addEventListener('shopify:section:load', () => {
    setTimeout(initTechnicalSheet, 100);
  });
})();

